import type { Ref } from "vue";

// Scroll-triggered reveal: walks DOM for any element tagged `data-reveal`
// and toggles `.is-visible` when it enters the viewport. CSS in main.css
// handles the fade + translateY transition and prefers-reduced-motion
// collapses the animation to instant-visible.
//
// Robustness contract:
// - `await nextTick()` before the first scan so v-if branches that flipped
//   on the same tick as mount (e.g. <div v-if="page"> after useAsyncData)
//   are in DOM.
// - A MutationObserver picks up [data-reveal] elements that mount LATER —
//   nested components, v-if branches that flip on a subsequent reactive
//   update, lazy `<ClientOnly>` children, etc. Without this, late targets
//   stay at opacity:0 forever because the initial scan missed them.
// - Both observers disconnect on unmount.
//
// Designed for opt-in use on marketing pages (e.g. /about). Pass `root`
// ref to scope the observer; omit to scan document-wide after mount.
export function useReveal(options?: {
  root?: Ref<HTMLElement | null>;
  threshold?: number;
  rootMargin?: string;
}) {
  onMounted(async () => {
    if (!("IntersectionObserver" in window)) return;

    await nextTick();

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const scope: ParentNode = options?.root?.value ?? document;
    const moRoot: Node =
      scope === document ? document.body : (scope as Node);

    const reveal = (el: HTMLElement) => el.classList.add("is-visible");

    if (reduced) {
      scope.querySelectorAll<HTMLElement>("[data-reveal]").forEach(reveal);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            reveal(e.target as HTMLElement);
            io.unobserve(e.target);
          }
        }
      },
      {
        threshold: options?.threshold ?? 0.15,
        rootMargin: options?.rootMargin ?? "0px 0px -80px 0px",
      },
    );

    // Skip IO for elements already in the viewport on mount: a tall block
    // can have <15% in view yet still be the first thing the user sees, and
    // the threshold-based callback would leave it at opacity:0 until the
    // first scroll.
    //
    // Layout-thrash discipline: collect ALL rects in one read pass, THEN
    // apply class writes / observer attachments. Mixing read+write per
    // element forces N forced-reflows on first scan and shows up as a
    // "Forced reflow" insight in PageSpeed. With batched reads, the entire
    // initial visibility pass is one synchronous layout flush.
    const viewportH = window.innerHeight;
    const classifyAndApply = (els: HTMLElement[]) => {
      if (els.length === 0) return;
      const rects = els.map(el => el.getBoundingClientRect()); // READ phase
      for (let i = 0; i < els.length; i++) {                    // WRITE phase
        const el = els[i];
        if (el.classList.contains("is-visible")) continue;
        const r = rects[i];
        if (r.top < viewportH && r.bottom > 0) reveal(el);
        else io.observe(el);
      }
    };

    classifyAndApply(
      Array.from(scope.querySelectorAll<HTMLElement>("[data-reveal]")),
    );

    const mo = new MutationObserver((mutations) => {
      const toClassify: HTMLElement[] = [];
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches("[data-reveal]")) toClassify.push(node);
          node
            .querySelectorAll<HTMLElement>("[data-reveal]")
            .forEach((el) => toClassify.push(el));
        });
      }
      classifyAndApply(toClassify);
    });
    mo.observe(moRoot, { childList: true, subtree: true });

    onScopeDispose(() => {
      mo.disconnect();
      io.disconnect();
    });
  });
}
